import { PathStyleExtension } from "@deck.gl/extensions";
import { GeoJsonLayer } from "@deck.gl/layers";
import { useQuery } from "@tanstack/react-query";
import { bbox } from "@turf/bbox";
import { bboxPolygon } from "@turf/bbox-polygon";
import { buffer } from "@turf/buffer";
import { point } from "@turf/helpers";
import type { Geometry } from "geojson";
import { useEffect, useMemo, useState } from "react";
import type { Site, UnmatchedDataPoint } from "../../apiClient/generated";
import { useMapApiClient } from "../../hooks";
import { createFeatureCollection } from "../../utils/geopatialUtils";
import {
    EMISSION_LAYER_STYLES,
    MAP_ZOOM_SHOW_DETAILS,
} from "../MapV2/constants";
import { PlumeOpacityPanel } from "../MapV2/elements/DataControls/PlumeControl";
import { useInfrastructureOnMap } from "../MapV2/hooks/infrastructure";
import { useMap } from "../MapV2/hooks/mapState";
import {
    EmissionRecordsLayers,
    PlumeOutlinesLayer,
} from "../MapV2/layers/emissions";
import { RasterLayer } from "../MapV2/layers/rasters";
import { MapBase } from "../MapV2/Map";
import { MapPopup, type PickedItems } from "../MapV2/MapPopup";

const CONTEXT_RADIUS_KM = 2;

// The outlines of the neighbouring unmatched data points are drawn as context,
// so the area around the data point is what gets fetched.
const contextArea = (dataPoint: UnmatchedDataPoint) => {
    const buffered = buffer(
        point(dataPoint.location.coordinates as [number, number]),
        CONTEXT_RADIUS_KM,
        { units: "kilometers" },
    );
    if (!buffered) {
        return undefined;
    }
    return bboxPolygon(bbox(buffered)).geometry;
};

// Outlined rather than filled, to keep the plume underneath readable. White
// under black dashes reads as a dashed border on any basemap.
const selectedSiteHighlight = (site?: Site | null) => {
    let geometry = site?.shape as Geometry | undefined;
    if (!geometry && site?.location?.coordinates) {
        geometry = buffer(
            point(site.location.coordinates as [number, number]),
            60,
            { units: "meters" },
        )?.geometry;
    }
    if (!geometry) {
        return [];
    }

    const shared = {
        data: {
            type: "FeatureCollection" as const,
            features: [{ type: "Feature" as const, properties: {}, geometry }],
        },
        filled: false,
        stroked: true,
        pickable: false,
        lineWidthUnits: "pixels" as const,
        getLineWidth: 3,
    };

    return [
        new GeoJsonLayer({
            ...shared,
            id: "unmatched-selected-site-outline",
            getLineColor: [255, 255, 255, 255],
        }),
        new GeoJsonLayer({
            ...shared,
            id: "unmatched-selected-site-dashes",
            getLineColor: [0, 0, 0, 255],
            getDashArray: [8, 8],
            dashJustified: true,
            extensions: [new PathStyleExtension({ dash: true })],
        }),
    ];
};

interface DecisionMinimapProps {
    dataPoint: UnmatchedDataPoint;
    selectedSite?: Site | null;
    // Omit to keep the map read-only.
    onSelectSite?: (siteId: string) => void;
}

export const DecisionMinimap = (props: DecisionMinimapProps) => {
    const mapId = `unmatched-${props.dataPoint.id}`;
    const { flyTo } = useMap(mapId);
    const mapApiClient = useMapApiClient();

    const [pickedSites, setPickedSites] = useState<PickedItems | undefined>();
    const [showPlumes, setShowPlumes] = useState(true);
    const [plumeOpacity, setPlumeOpacity] = useState(0.5);
    const [oldPlumeOpacity, setOldPlumeOpacity] = useState(0);

    const area = useMemo(() => contextArea(props.dataPoint), [props.dataPoint]);

    // Deferred so deck.gl has sized the canvas first.
    useEffect(() => {
        const coordinates = props.dataPoint.location?.coordinates;
        if (!coordinates) {
            return;
        }
        const timeout = setTimeout(() => {
            flyTo(coordinates[1], coordinates[0], 15, true);
        }, 300);
        return () => clearTimeout(timeout);
    }, [props.dataPoint, flyTo]);

    const { infrastructureLayers } = useInfrastructureOnMap(
        mapId,
        true,
        useMemo(() => ["SITE" as const, "EQUIPMENT_GROUP" as const], []),
        undefined,
        useMemo(() => ({}), []),
    );

    const plumesQuery = useQuery({
        queryKey: ["unmatchedPlumeImages", props.dataPoint.id],
        enabled: !!props.dataPoint.plumeImage,
        queryFn: async () => {
            const response = await mapApiClient.mapUnmatchedDataPlumesList({
                dataPoints: [props.dataPoint.id],
            });
            return response.results;
        },
    });

    const outlinesQuery = useQuery({
        queryKey: ["unmatchedPlumeOutlines", props.dataPoint.id],
        enabled: !!area,
        queryFn: async () => {
            const response =
                await mapApiClient.mapUnmatchedDataPlumeOutlinesList({
                    locationWithin: JSON.stringify(area),
                });
            return response.results;
        },
    });

    const layers = useMemo(
        () => [
            ...infrastructureLayers,
            PlumeOutlinesLayer(
                (outlinesQuery.data || []).filter(
                    (p) => !showPlumes || !p.hasPlume,
                ),
                "unmatched_plume_outlines",
                true,
                MAP_ZOOM_SHOW_DETAILS + 1,
                plumeOpacity,
            ),
            RasterLayer({
                data: plumesQuery.data || [],
                name: "unmatched_plume_images",
                visible: showPlumes,
                zoomLevel: MAP_ZOOM_SHOW_DETAILS + 1,
                opacity: plumeOpacity,
                detail: true,
            }),
            EmissionRecordsLayers(
                createFeatureCollection([props.dataPoint]).features,
                true,
                MAP_ZOOM_SHOW_DETAILS + 1,
                "unmatched_detection",
                EMISSION_LAYER_STYLES.operatorProvided,
            ),
            ...selectedSiteHighlight(props.selectedSite),
        ],
        [
            infrastructureLayers,
            outlinesQuery.data,
            plumesQuery.data,
            showPlumes,
            plumeOpacity,
            props.dataPoint,
            props.selectedSite,
        ],
    );

    return (
        <div className="relative h-[400px] overflow-hidden rounded border border-ae-gray-250">
            <MapBase
                mapId={mapId}
                layers={[layers]}
                cursor={props.onSelectSite ? "pointer" : undefined}
                onLeftClick={
                    props.onSelectSite
                        ? ({ info }) => {
                              const sites = info.filter(
                                  (i) =>
                                      i.layer.id.includes(
                                          "infrastructure-sites-points",
                                      ) ||
                                      i.layer.id.includes(
                                          "infrastructure-sites-shapes",
                                      ),
                              );
                              const siteClusters = info.filter((i) =>
                                  i.layer.id.includes(
                                      "infrastructure-sites-clusters",
                                  ),
                              );

                              if (
                                  sites.length === 0 &&
                                  siteClusters.length === 0
                              ) {
                                  setPickedSites(undefined);
                                  return;
                              }

                              if (
                                  sites.length === 1 &&
                                  siteClusters.length === 0
                              ) {
                                  setPickedSites(undefined);
                                  props.onSelectSite(
                                      sites[0].object.properties.id,
                                  );
                                  return;
                              }

                              // MapPopup expands clusters into their member
                              // sites, so they need no separate handling.
                              setPickedSites({
                                  // Canvas-relative: the popup is positioned
                                  // absolutely inside the map box.
                                  pointClicked: {
                                      x: info[0].x,
                                      y: info[0].y,
                                  },
                                  sites,
                                  siteClusters,
                                  equipment: [],
                                  pipelines: [],
                                  emissions: [],
                                  emissionClusters: [],
                                  dataPoints: [],
                                  unmatchedDataPoints: [],
                                  extraData: [],
                              });
                          }
                        : undefined
                }
                getTooltip={(info) => {
                    const properties = info.object?.properties;
                    if (!properties) {
                        return null;
                    }
                    if ("siteName" in properties) {
                        return properties.siteName;
                    }
                    if ("detectedRate" in properties) {
                        return "Unmatched detection";
                    }
                    return null;
                }}
                showScaleControl
                showZoomControl
                showBasemapControl
            />

            {!!pickedSites && (
                <MapPopup
                    picked={pickedSites}
                    onClickItem={(item) => {
                        setPickedSites(undefined);
                        const siteId = item.object?.properties?.id;
                        if (siteId) {
                            props.onSelectSite?.(siteId);
                        }
                    }}
                />
            )}

            {!!props.dataPoint.plumeImage && (
                <div className="absolute top-2 w-full flex justify-center">
                    <PlumeOpacityPanel
                        className="w-4/5 overflow-x-auto"
                        plumeOpacity={plumeOpacity}
                        showPlumes={showPlumes}
                        onOpacityChange={setPlumeOpacity}
                        onToggleVisibility={() => {
                            if (plumeOpacity === 0) {
                                setPlumeOpacity(oldPlumeOpacity || 0.5);
                                setOldPlumeOpacity(0);
                            } else {
                                setOldPlumeOpacity(plumeOpacity);
                                setPlumeOpacity(0);
                            }
                        }}
                        onShowPlumesChange={() => setShowPlumes(!showPlumes)}
                    />
                </div>
            )}
        </div>
    );
};
